import { useMemo } from "react"

import { getCoreRowModel, useReactTable } from "@tanstack/react-table"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

import { EntityTable } from "@/components/EntityTable"
import Icon from "@/components/Icon"
import { STATUS_LABELS, incentiveColumns } from "@/components/incentives/incentiveColumns"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { MultiSelect } from "@/components/ui/multi-select"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Spinner } from "@/components/ui/spinner"
import {
  INCENTIVE_STATUSES,
  type IncentiveListRow,
  type IncentiveStatus,
  useIncentiveAudienceOptions,
  useIncentives,
} from "@/hooks/useIncentives"

import { Constants } from "../../../../database.types"

const DEFAULT_STATUSES: IncentiveStatus[] = ["draft", "live"]

// useReactTable re-renders whenever `data` changes identity, so a fresh [] while loading would loop.
const NO_INCENTIVES: IncentiveListRow[] = []

// No status param means the default; an empty one means the user cleared them all to see every status.
const statusesFrom = (param: string | null): IncentiveStatus[] => {
  if (param === null) return DEFAULT_STATUSES
  return INCENTIVE_STATUSES.filter((status) => param.split(",").includes(status))
}

export default function IncentivesContent() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const depot = searchParams.get("depot") ?? "all"
  const driverType = searchParams.get("driver_type") ?? "all"
  const statusParam = searchParams.get("status")

  const setParam = (key: string, value: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === null) {
          next.delete(key)
        } else {
          next.set(key, value)
        }
        return next
      },
      { replace: true },
    )

  // A fresh object here would be a fresh query key on every render.
  const filters = useMemo(
    () => ({
      depot: depot === "all" ? undefined : depot,
      driverType: driverType === "all" ? undefined : driverType,
      statuses: statusesFrom(statusParam),
    }),
    [depot, driverType, statusParam],
  )

  const { data: incentives, isLoading, error } = useIncentives(filters)
  const { data: audienceOptions } = useIncentiveAudienceOptions()

  const table = useReactTable({
    data: incentives ?? NO_INCENTIVES,
    columns: incentiveColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const count = incentives?.length ?? 0

  return (
    <div>
      {error && (
        <Alert variant="destructive" className="mb-4">
          <Icon name="warningCircleFilled" className="h-4 w-4" />
          <AlertDescription>
            Error loading incentives: {error instanceof Error ? error.message : "Unknown error"}
          </AlertDescription>
        </Alert>
      )}

      <div className="pb-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-palette-jungleGreen900">Incentives</h2>
          <div className="flex items-center gap-2">
            <Button variant="outline" asChild>
              <Link to="/incentives/reconcile">
                <Icon name="checkCircle" className="h-4 w-4 text-current" />
                Reconcile incentives
              </Link>
            </Button>
            <Button asChild>
              <Link to="/incentives/new">
                <Icon name="plus" className="h-4 w-4 text-current" />
                New incentive
              </Link>
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="flex flex-wrap items-end gap-4 p-4">
            <div className="space-y-2">
              <Label htmlFor="depot-filter">Depot</Label>
              <Select
                value={depot}
                onValueChange={(value) => setParam("depot", value === "all" ? null : value)}
              >
                <SelectTrigger id="depot-filter" className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any depot</SelectItem>
                  {audienceOptions?.depots.map((depot) => (
                    <SelectItem key={depot.value} value={depot.value}>
                      {depot.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[220px] space-y-2">
              <Label>Status</Label>
              <MultiSelect
                options={INCENTIVE_STATUSES.map((status) => ({
                  value: status,
                  label: STATUS_LABELS[status],
                }))}
                selected={filters.statuses}
                onChange={(statuses) => setParam("status", statuses.join(","))}
                placeholder="All statuses"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="driver-type-filter">Driver type</Label>
              <Select
                value={driverType}
                onValueChange={(value) => setParam("driver_type", value === "all" ? null : value)}
              >
                <SelectTrigger id="driver-type-filter" className="w-[180px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any driver type</SelectItem>
                  {Constants.public.Enums.person_type.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="relative overflow-hidden rounded-lg bg-white shadow-sm">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80">
            <Spinner size="lg" />
            <span className="ml-2 text-palette-jungleGreen700">Loading incentives...</span>
          </div>
        )}
        <EntityTable
          table={table}
          getRowProps={(incentive) => ({
            key: incentive.id,
            onClick: () => navigate(`/incentives/${incentive.id}`),
          })}
          footer={`${count} ${count === 1 ? "incentive" : "incentives"}`}
        />
      </div>
    </div>
  )
}
